import { useEffect, useState } from 'react';

import { Button, Modal, Table } from 'antd';
import Title from 'antd/lib/typography/Title';
import TextArea from 'antd/lib/input/TextArea';
import { QuestionCircleOutlined } from '@ant-design/icons';

import notifier from 'utils/notifier';
import { ErrorResponse, SubmittedClaim } from 'utils/types';
import { EDI_CLAIM_NON_REJECTED_STATUSES, EDI_REJECTED_STATUS, EDI_INPROCESS_STATUS } from 'utils/objects';
import { getDisplayCompanyId } from 'utils/helpers';
import { ClaimList } from 'utils/enums';
import FloatLabel from 'components/Common/FloatLabel';
import SubmittedClaimsForm from '../SubmittedClaimsForm';

import './index.scss';
import { isNumber } from 'lodash';

type SubmittedClaimsTableProps = {
  dataSource: SubmittedClaim[],
  isFetchingSubmitted: boolean,
  cancel: any,
  isCanceling: boolean,
  setPage(page: string): void,
  setClaimNo(claimNo: string): void,
  setCompanyId(companyId: string): void,
};

const SubmittedClaimsTable = (props: SubmittedClaimsTableProps) => {

  const [cancelModalVisible, setCancelModalVisible] = useState(false);
  const [cancelReason, setCancelReason] = useState('');
  const [claimNo, setClaimNo] = useState('');
  const [companyId, setCompanyId] = useState('');
  const [submission, setSubmission] = useState(false);
  const [legendClicked, setLegendClicked] = useState(false);

  const showLegendText = () => {
    Modal.info(
      {
        title: <><b>Status Legend</b></>,
        content: <>
                  <span>
                    <b>Accepted:</b> Initial claim review passed, see claim detail for final result<br/>
                  </span>
                  <span>
                    <b>Rejected:</b> Incorrect Claim Information, click "Rejected" under the Status column of the claim record to see reason<br/>
                  </span>
                  <span>
                    <b>In Process/Pending:</b> Claim under initial review<br/>
                  </span> 
                  <span>
                    <b>Cancelled:</b> Claim cancelled by the user<br/>
                  </span>                  
                </>,
        type: "info",
        okText: "OK",
        width: '850px',
        maskClosable: false,
        onOk: () => {
          setLegendClicked(false); // Reset the flag when the modal is closed
        },
      });
  } 

  const getStatusLegend = () => (
      <Button 
        type="link" 
        className="help-link links"
        style={{ padding: 0, border: 'none', verticalAlign: 'top'}} // Adjust the line height       
      >
        <span
          onClick={(e) => {
            e.stopPropagation(); // Prevent the table header click event
            setLegendClicked(true);
            showLegendText();
          }} 
        >
          <QuestionCircleOutlined style={{ fontSize: '14px',color: '#1890ff', verticalAlign: 'top'}}/>
        </span>
          
      </Button>    
  );

  const submittedClaimsColumns = [
    {
      title: 'IPA',
      key: 'RmiCompanyId',
      dataIndex: 'RmiCompanyId',
      render: (text: string) => getDisplayCompanyId(text)
    },
    {
      title: 'Claim Reference No',
      key: 'RmiClaimReferenceNo',
      dataIndex: 'RmiClaimReferenceNo',
      sorter: (a: any, b: any) => a.RmiClaimReferenceNo.localeCompare(b.RmiClaimReferenceNo),
      render: (text: string, record: any) => <a onClick={() => handleClaimDetailOpen(record)}>{text}</a>
    },
    {
      title: 'Claim No',
      key: 'RmiEzClaimNo',
      dataIndex: 'RmiEzClaimNo',
      sorter: (a: any, b: any) => a?.RmiEzClaimNo?.localeCompare(b?.RmiEzClaimNo),
      render: (text: string) => text === 'CANCELLED' ? '' : text
    },
    {
      title: 'Member Id',
      key: 'RmiMemberId',
      dataIndex: 'RmiMemberId',
      sorter: (a: any, b: any) => a.RmiMemberId.localeCompare(b.RmiMemberId),
    },
    {
      title: 'Member Name',
      key: 'RmiPatientName',
      dataIndex: 'RmiPatientName',
      sorter: (a: any, b: any) => a.RmiPatientName.localeCompare(b.RmiPatientName),
    },
    {
      title: 'Date Received',
      key: 'RmiDateReceived',
      dataIndex: 'RmiDateReceived',
      sorter: (a: any, b: any) => a.RmiDateReceived - b.RmiDateReceived, //TODO
      render: (text: string) => text.replace(/(\d{4})(\d{2})(\d{2})/g, '$1/$2/$3')
    },
    {
      title: 'Plan',
      key: 'RmiInsuredPlan',
      dataIndex: 'RmiInsuredPlan',
    },
    {
      title: 'Cancel',
      key: 'RmiEzClaimNo',
      dataIndex: 'RmiEzClaimNo',
      render: (text: string, record: any) => record.RmiEdiGenerated !== 'N' ? null : <a onClick={()=> renderCancelModal(record.RmiClaimReferenceNo, record.RmiCompanyId)}>{text !== 'CANCELLED' && record.RmiEdiGenerated === 'N' ? 'Cancel' : ''}</a>
    },
    {
      title: (
        <>
          Status {getStatusLegend()}
        </>
      ),
      key: 'RmiStatus',
      dataIndex: 'RmiStatus',
      sorter: (a: any, b: any) => {
        if (legendClicked) {
          return 0; // Prevent sorting when legend is clicked
        }
        a = a.RmiStatus || '';
        b = b.RmiStatus || '';
        return a.localeCompare(b);
      },
      render: (_: string, record: any) => getStatus(record)
    },
  ];

  const getStatus = (record: any) => {
    const status = record.RmiStatus;
    const pipeIndex = status?.indexOf("|");
    if (!status || (!EDI_CLAIM_NON_REJECTED_STATUSES.includes(status?.toUpperCase()) && !status?.toUpperCase().includes(EDI_REJECTED_STATUS)) || !pipeIndex) {
      return record.RmiEzClaimNo;
    }
    if (pipeIndex && pipeIndex > 0) {
      const error = status?.substring(pipeIndex + 1, status?.length);
      const claimStatus = status?.toUpperCase().substring(0, pipeIndex);
      return <a onClick={()=> showClaimErrorDescription(error)}>{claimStatus}</a>
    } 
    
    return status;
  }


  const showClaimErrorDescription = (error: string) => {
    return Modal.info(
      {
        title: "Claim Error",
        content: error,
        type: "info",
        maskClosable: false,
      });
  }

  const handleClaimDetailOpen = (record: any) => {
    props.setCompanyId(record.RmiCompanyId);
    props.setClaimNo(record.RmiClaimReferenceNo);
    props.setPage('detail');
  };

  const exitCancelModal = () => {
    setCancelModalVisible(false);
    setCancelReason('');
    setClaimNo('');
    setCompanyId('');
  };

  const renderCancelModal = (claimNo: string, companyId: string) => {
    setCancelModalVisible(true);
    setCancelReason('');
    setClaimNo(claimNo);
    setCompanyId(companyId);
  };

  const SUCCESS = 1;

  const submitCancelClaim = () => {
    const cancelClaimObject = {
      claimNo,
      companyId,
      notes: cancelReason,
      userId: sessionStorage.user_id,
    };
    return props.cancel(cancelClaimObject)
      .then((response: number) => {
        if (response === SUCCESS) {
          setSubmission(!submission);
          exitCancelModal();
          notifier.success(`Successfully canceled Claim #${claimNo}.`);
        }
        else notifier.error('Something went wrong, please contact a system administrator.');
      })
      .catch((err: ErrorResponse) => {
        notifier.error(err?.response?.data?.Message || 'Something went wrong, please contact a system administrator.');
      });
  }

  return (
    <>
      <Table
        columns={submittedClaimsColumns}
        dataSource={props.dataSource}
        pagination={{ position: ['bottomRight'], showSizeChanger: true }}
        loading={props.isFetchingSubmitted || props.isCanceling}
        rowKey="RmiClaimReferenceNo"
      />

      <Modal
        visible={cancelModalVisible}
        onCancel={exitCancelModal}
        onOk={submitCancelClaim}
        className="modal-width"
      >
        <div className="container">
          <Title level={3}>Claim Cancellation Notes</Title>
        </div>
        <FloatLabel label="Please let us know why this claim is cancelled." value={cancelReason}>
          <TextArea
            value={cancelReason}
            onChange={e => setCancelReason(e.target.value)}
            rows={4}
            />
        </FloatLabel>
      </Modal>
    </>
  );
};

export default SubmittedClaimsTable;